"use client";

import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { REACT_QUERY_KEY } from "~/core/constants/common";
import type { PaginationOptions } from "~/core/types/common.type";

import useCurrentOrganization from "~/lib/organizations/hooks/use-current-organization";
import { SocketEvents } from "~/lib/socket/constants";
import { useSocketConnection } from "~/lib/socket/hooks/useSocketConnection";

import { getAllOpportunities } from "../apis/client";
import { defaultOpportunityResponseQuery } from "../constants/common";
import { useOpportunitiesStore } from "../store";
import type { Opportunity } from "../types/common";
import { mapOpportunityAttributeData } from "../utils/map-attribute-data";

export const useOpportunitiesData = () => {
  const invalidateOpportunitiesData = useInvalidateOpportunitiesData();

  const queryParams = useOpportunitiesStore(state => state.query);
  const withDefaultView = queryParams.filters?.length !== 0;
  const activePipeline = useOpportunitiesStore(state => state.activePipeline);
  const setNewOpportunityIds = useOpportunitiesStore(
    state => state.setNewOpportunityIds,
  );
  const setLoadingTableOpportunities = useOpportunitiesStore(
    state => state.setLoadingTableOpportunities,
  );

  const storeToOpportunitiesStore = useCallback(
    (opportunities: Opportunity[], pagination: PaginationOptions) => {
      useOpportunitiesStore.setState(state => {
        const updatedPages = {
          ...state.fetchedPages,
          [pagination.page]: opportunities,
        };

        // Reconstruct opportunities in proper page order
        const sortedPageNumbers = Object.keys(updatedPages)
          .map(Number)
          .sort((a, b) => a - b);

        const combinedOpportunities = sortedPageNumbers.flatMap(
          page => updatedPages[page],
        );

        // Ensure uniqueness
        const uniqueOpportunityMap = new Map(
          combinedOpportunities.map(opp => [opp.id, opp]),
        );

        const uniqueOpportunities = Array.from(uniqueOpportunityMap.values());
        const currentActiveOpportunity = state.activeOpportunityData;
        const doesCurrentActiveOpportunityExistInOpportunities =
          currentActiveOpportunity &&
          uniqueOpportunities.findIndex(
            opp => opp.id === currentActiveOpportunity.id,
          ) !== -1;
        let updatedActiveOpportunityData = currentActiveOpportunity;

        if (!doesCurrentActiveOpportunityExistInOpportunities) {
          updatedActiveOpportunityData =
            uniqueOpportunities.length > 0 ? uniqueOpportunities[0] : null;
        }

        return {
          fetchedPages: updatedPages,
          opportunities: uniqueOpportunities,
          pagination: {
            limit: pagination.limit,
            page: pagination.page,
            total: pagination.total,
          },
          activeOpportunityData: updatedActiveOpportunityData,
        };
      });
    },
    [],
  );

  const {
    data: opportunitiesData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [
      REACT_QUERY_KEY.ALL_OPPORTUNITIES_LISTS,
      activePipeline?.pipeline_id,
      queryParams.filters ? JSON.stringify(queryParams.filters) : null,
      queryParams.sorting ? JSON.stringify(queryParams.sorting) : null,
      queryParams.q,
    ],
    queryFn: ({ pageParam, queryKey }) => {
      const [_, activePipelineId, filtersString, sortingString, q] = queryKey;
      const filters = filtersString
        ? JSON.parse(filtersString as string)
        : undefined;
      const sorting = sortingString
        ? JSON.parse(sortingString as string)
        : undefined;

      return getAllOpportunities({
        filters,
        sorting,
        withDefaultView,
        pipeline_id: activePipelineId as string,
        page: pageParam as number,
        q: q as string,
        limit: defaultOpportunityResponseQuery.limit,
      });
    },
    getNextPageParam: lastPage => {
      const lastPageMetadata = lastPage?.metadata;
      const total = lastPageMetadata?.total ?? 0;
      const page = lastPageMetadata?.page ?? 0;
      const limit = defaultOpportunityResponseQuery.limit as number;
      const totalPages = Math.ceil(total / limit);

      return page && page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    placeholderData: keepPreviousData,
  });

  const currentOrg = useCurrentOrganization();

  //Socket event
  const event = SocketEvents.opportunity.listUpdated;
  const room = `${event}:room:${currentOrg?.org_id}`;
  useSocketConnection<{ opportunity_id: string[] }>({
    event,
    room,
    messageCallback: data => {
      invalidateOpportunitiesData();
      setNewOpportunityIds(data.data.opportunity_id);
    },
  });

  useEffect(() => {
    setLoadingTableOpportunities(
      isLoading ? "first-time" : isFetchingNextPage ? "next-page" : null,
    );
  }, [isLoading, isFetchingNextPage, setLoadingTableOpportunities]);

  useEffect(() => {
    if (!opportunitiesData) return;

    const mappedOpportunities = mapOpportunityAttributeData(
      opportunitiesData.pages.flatMap(page => page?.opportunities ?? []),
    );
    const lastPage =
      opportunitiesData.pages[opportunitiesData.pages.length - 1];
    storeToOpportunitiesStore(
      mappedOpportunities,
      lastPage?.metadata ?? {
        page: lastPage?.metadata?.page ?? 1,
        total: lastPage?.metadata?.total ?? 0,
        limit: defaultOpportunityResponseQuery.limit as number,
      },
    );
  }, [opportunitiesData, storeToOpportunitiesStore]);

  return {
    hasNextPage,
    fetchNextPage,
    opportunitiesData,
  };
};

export const useInvalidateOpportunitiesData = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.ALL_OPPORTUNITIES_LISTS],
    });
  };
};
